import { useCallback } from 'react';
import * as Cesium from 'cesium';

/**
 * Hook for managing camera controls.
 * @param {React.RefObject} viewerRef - Reference to the Cesium viewer
 * @param {React.RefObject} tilesetRef - Reference to the point cloud tileset
 */
export const useCameraControl = (viewerRef, tilesetRef) => {
  const recenterCamera = useCallback(() => {
    const viewer = viewerRef.current;
    const tileset = tilesetRef.current;

    if (!viewer || !tileset) {
      console.warn('Viewer or tileset not available for camera recentering');
      return;
    }

    try {
      viewer.flyTo(tileset, {
        duration: 1.5,
        offset: new Cesium.HeadingPitchRange(0, -0.5, 0)
      });
    } catch (error) {
      console.error('Error recentering camera:', error);
    }
  }, [viewerRef, tilesetRef]);

  const updateCameraFOV = useCallback((fovDegrees) => {
    const viewer = viewerRef.current;

    if (!viewer) {
      console.warn('Viewer not available for FOV update');
      return;
    }

    try {
      const fovRadians = Cesium.Math.toRadians(Math.max(15, Math.min(120, fovDegrees)));
      viewer.camera.frustum.fov = fovRadians;
    } catch (error) {
      console.error('Error updating camera FOV:', error);
    }
  }, [viewerRef]);

  const updateCameraMovement = useCallback((zoomFactor) => {
    const viewer = viewerRef.current;

    if (!viewer) {
      console.warn('Viewer not available for camera movement update');
      return;
    }

    try {
      const controller = viewer.scene.screenSpaceCameraController;
      controller.zoomFactor = Math.max(0.1, Math.min(10.0, zoomFactor));
    } catch (error) {
      console.error('Error updating camera movement parameters:', error);
    }
  }, [viewerRef]);

  return {
    recenterCamera,
    updateCameraFOV,
    updateCameraMovement,
  };
};
